
// GuardLocationTable.js
import React, { useState, useMemo, useEffect } from "react";
import CompactDataTable from "../../components/Common/Datatable/BasicDatatble";
import {
  Input,
  Row,
  Col,
  Button,
  Card,
  CardBody,
  Badge,
  ButtonGroup,
} from "reactstrap";
import * as XLSX from "xlsx";
import DataTable from "react-data-table-component";
import { formatDate } from "../../helpers/formatDate";

const GuardLocationTable = ({ locations, type = "guards" }) => {
  const [searchText, setSearchText] = useState("");
  const [timeFilter, setTimeFilter] = useState("all");

  // Time filter options
  const timeFilters = [
    { value: "all", label: "All", color: "secondary" },
    { value: "online", label: "Online (1hr)", color: "success" },
    { value: "away", label: "Away (1.5d)", color: "warning" },
    { value: "offline", label: "Offline", color: "danger" },
  ];

 

  // Helper function to get status
  const getStatus = (updatedAt) => {
    if (!updatedAt) return { color: "red", status: "offline" };
    
    const timeDifference = Date.now() - new Date(updatedAt);
    if (timeDifference <= 3600 * 1000) return { color: "green", status: "online" };
    if (timeDifference <= 1.5 * 24 * 3600 * 1000) return { color: "orange", status: "away" };
    return { color: "red", status: "offline" };
  };

  // Define table columns
  const columns = [
    {
      name: "Status",
      cell: (row) => {
        const { color } = getStatus(row.updatedAt);
        return (
          <span
            className="status-indicator"
            style={{
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              backgroundColor: color,
              display: "inline-block",
              boxShadow: `0 0 8px ${color}40`,
            }}
          />
        );
      },
      width: "80px",
      center: true,
    },
    {
      name: "Name/Details",
      cell: (row) => (
        <div>
          <div className="fw-semibold">
            {row?.vehicleNumber
              ? `${row.vehicleNumber}`
              : `${row.firstName} ${row.lastName}`}
          </div>
         
          {row?.vehicleNumber && (
            <small className="text-muted d-block">
              Driver: {row.firstName} {row.lastName}
            </small>
          )}
        </div>
      ),
      sortable: true,
      minWidth: "200px",
    },
    {
      name: "Contact",
      cell: (row) => (
        <div>
          {row?.contactNumber ? (
            <a href={`tel:${row.contactNumber}`} className="text-decoration-none">
              <i className="fas fa-phone me-1"></i>
              {row.contactNumber}
            </a>
          ) : (
            <span className="text-muted">-</span>
          )}
        </div>
      ),
      sortable: true,
      minWidth: "150px",
    },
    {
      name: "Last Seen",
      cell: (row) => (
        <div>
          <div className="fw-medium">
            {formatDate(row.updatedAt)}
          </div>
          {row?.currentLocation?.updatedAt && (
            <small className="text-muted">
              {new Date(row.currentLocation.updatedAt).toLocaleString()}
            </small>
          )}
        </div>
      ),
      sortable: true,
      sortFunction: (a, b) => {
        const dateA = new Date(a.updatedAt || 0);
        const dateB = new Date(b.updatedAt || 0);
        return dateB - dateA;
      },
      minWidth: "180px",
    },
    {
      name: "Location",
      cell: (row) => (
        <div>
          {row?.currentLocation?.latitude && row?.currentLocation?.longitude ? (
            <Button
              size="sm"
              color="outline-primary"
              onClick={() => {
                const url = `https://maps.google.com/?q=${row.currentLocation.latitude},${row.currentLocation.longitude}`;
                window.open(url, "_blank");
              }}
            >
              <i className="fas fa-map-marker-alt me-1"></i>
              View Map
            </Button>
          ) : (
            <span className="text-muted">No location</span>
          )}
        </div>
      ),
      minWidth: "120px",
    },
  ];

  // Transform locations
  const data = useMemo(() => {
    return locations?.map((guardLocation) => ({
      firstName: guardLocation.firstName,
      lastName: guardLocation.lastName,
      role: guardLocation.role,
      divisionName: guardLocation.divisionName || "All",
      rangeName: guardLocation.rangeName || "All",
      roundName: guardLocation.roundName || "All",
      vehicleNumber: guardLocation.vehicleNumber,
      contactNumber: guardLocation.contactNumber,
      updatedAt: guardLocation.currentLocation?.updatedAt,
      currentLocation: guardLocation.currentLocation,
      original: guardLocation,
    })) || [];
  }, [locations]);

  // Filter data based on search input and time filter
  const filteredData = useMemo(() => {
    let filtered = data;

    // Apply search filter
    if (searchText) {
      const lowerSearch = searchText.toLowerCase();
      filtered = filtered.filter((item) => {
        const name = `${item.firstName} ${item.lastName}`.toLowerCase();
        const vehicle = item.vehicleNumber?.toLowerCase() || "";
        const role = item.role?.toLowerCase() || "";
        const contact = item.contactNumber?.toLowerCase() || "";
        return (
          name.includes(lowerSearch) ||
          vehicle.includes(lowerSearch) ||
          role.includes(lowerSearch) ||
          contact.includes(lowerSearch)
        );
      });
    }

    // Apply time filter
    if (timeFilter !== "all") {
      filtered = filtered.filter((item) => {
        const { status } = getStatus(item.updatedAt);
        return status === timeFilter;
      });
    }

    return filtered;
  }, [data, searchText, timeFilter]);

  console.log("Filtered Data:", filteredData);
  // Count status for summary
  const statusCounts = useMemo(() => {
    const counts = { online: 0, away: 0, offline: 0, total: data.length };
    
    data.forEach((row) => {
      const { status } = getStatus(row.updatedAt);
      counts[status]++;
    });
    
    return counts;
  }, [data]);

  // Export to Excel function
  const exportToExcel = () => {
    const exportData = filteredData.map((row, index) => ({
      "S.No": index + 1,
      "Name": `${row.firstName} ${row.lastName}`,
      // "Role": row.role || "-",
      "Division": row.divisionName || "All",
      "Range": row.rangeName || "All",
      "Round": row.roundName || "All",

      "Contact Number": row.contactNumber || "-",
      "Vehicle Number": row.vehicleNumber || "-",
      "Status": getStatus(row.updatedAt).status.toUpperCase(),
      "Last Seen": formatDate(row.updatedAt),
      "Last Update Time": row?.currentLocation?.updatedAt
        ? new Date(row.currentLocation.updatedAt).toLocaleString()
        : "-",
      "Latitude": row?.currentLocation?.latitude || "-",
      "Longitude": row?.currentLocation?.longitude || "-",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, type === "guards" ? "Guards" : "Vehicles");
    
    const fileName = `${type}_location_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  return (
    <div>
      {/* Summary Cards */}
      <Row >
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <CardBody className="text-center">
              <div className="d-flex align-items-center justify-content-center">
                <span className="status-dot me-2" style={{ backgroundColor: "green" }}></span>
                <div>
                  <h4 className="mb-0 text-success">{statusCounts.online}</h4>
                  <small className="text-muted">Online</small>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <CardBody className="text-center">
              <div className="d-flex align-items-center justify-content-center">
                <span className="status-dot me-2" style={{ backgroundColor: "orange" }}></span>
                <div>
                  <h4 className="mb-0 text-warning">{statusCounts.away}</h4>
                  <small className="text-muted">Away</small>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <CardBody className="text-center">
              <div className="d-flex align-items-center justify-content-center">
                <span className="status-dot me-2" style={{ backgroundColor: "red" }}></span>
                <div>
                  <h4 className="mb-0 text-danger">{statusCounts.offline}</h4>
                  <small className="text-muted">Offline</small>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <CardBody className="text-center">
              <div className="d-flex align-items-center justify-content-center">
                <i className="fas fa-users me-2 text-primary"></i>
                <div>
                  <h4 className="mb-0 text-primary">{statusCounts.total}</h4>
                  <small className="text-muted">Total</small>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Filters and Search */}
      <Card className="border-0 shadow-sm mb-4">
        <CardBody>
          <Row className="align-items-center">
            <Col md={6}>
              <Input
                type="text"
                placeholder={`Search ${type} by name, vehicle number, contact...`}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="form-control-lg"
              />
            </Col>
            <Col md={4}>
              <ButtonGroup className="w-100">
                {timeFilters.map((filter) => (
                  <Button
                    key={filter.value}
                    color={timeFilter === filter.value ? filter.color : "outline-secondary"}
                    onClick={() => setTimeFilter(filter.value)}
                    size="sm"
                  >
                    {filter.label}
                  </Button>
                ))}
              </ButtonGroup>
            </Col>
            <Col md={2}>
              <Button
                color="success"
                onClick={exportToExcel}
                className="w-100"
                disabled={filteredData.length === 0}
              >
                <i className="fas fa-file-excel me-2"></i>
                Export Excel
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Results Summary */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <Badge color="info" className="me-2">
            {filteredData.length} of {data.length} {type}
          </Badge>
          {timeFilter !== "all" && (
            <Badge color="secondary">
              Filter: {timeFilters.find(f => f.value === timeFilter)?.label}
            </Badge>
          )}
        </div>
      </div>

      {/* Data Table */}
      <Card className="border-0 shadow-sm">
        <CardBody className="p-0">
          <DataTable
            columns={columns}
            data={filteredData}
            highlightOnHover
            pointerOnHover
            pagination
            paginationPerPage={25}
            paginationRowsPerPageOptions={[25, 50, 100, 200, 300]}
            noDataComponent={
              <div className="text-center py-5">
                <i className="fas fa-search text-muted mb-3" style={{ fontSize: "3rem" }}></i>
                <h5 className="text-muted">No {type} found</h5>
                <p className="text-muted">Try adjusting your search or filter criteria</p>
              </div>
            }
          />
        </CardBody>
      </Card>

      <style jsx>{`
        .status-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          display: inline-block;
        }
        
        .status-indicator {
          animation: pulse 2s infinite;
        }
        
        @keyframes pulse {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
};
export default GuardLocationTable;

